import { useEffect, useMemo, useRef, useState } from "react";
import { GridColDef, GridColumnVisibilityModel } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { useForm } from "react-hook-form";
import { AiFillFileExcel } from "react-icons/ai";

import MainCard from "components/MainCard";
import StyledDataGrid from "components/@extended/StyledDataGrid";
import { SelectField } from "components/@extended/SelectField";
import LoadingButton from "components/@extended/LoadingButton";
import { IDropdownModel } from "common/models/IDropdownModel";
import { GetMemberTypeValues } from "features/memberType/api/hooks";
import { GetMaterialTypeValues } from "features/materialtype/api/hooks";
import api from "utils/api";

import { GetTopFrequentlyUsedBooks } from "../api/hooks";
import useTopBooksPerformanceStore from "../store/TopBooksPerformanceStore";
import { resolveDateRange } from "../store/dateRangeHelper";
import { ITopFrequentlyUsedBooks } from "../api/types";
import TopBooksChart from "./TopBooksChart";
import { ExportButton } from "components/ExportButton";

interface ITopBooksFilterFormValues {
  memberTypeID: string | null;
  materialTypeID: string | null;
}

const TopBooksSection = () => {
  const {
    dateRangePreset,
    customFromDate,
    customToDate,
    topBooksPostModel,
    handleTopBooksFiltering,
    handleTopBooksPagination,
    handleTopBooksSorting,
  } = useTopBooksPerformanceStore();

  const { fromDate, toDate } = useMemo(
    () => resolveDateRange(dateRangePreset, customFromDate, customToDate),
    [dateRangePreset, customFromDate, customToDate],
  );

  // Keep the shared date range in sync with the page-level filter, without
  // touching the Member/Material Type selections the user has made here.
  useEffect(() => {
    handleTopBooksFiltering({
      memberTypeID: topBooksPostModel.filterModel?.memberTypeID ?? null,
      materialTypeID: topBooksPostModel.filterModel?.materialTypeID ?? null,
      fromDate,
      toDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const { data: response, isFetching } =
    GetTopFrequentlyUsedBooks(topBooksPostModel);

  const rowCountRef = useRef(response?.apiData?.count || 0);
  const rowCount = useMemo(() => {
    if (response?.apiData?.count !== undefined) {
      rowCountRef.current = response?.apiData?.count;
    }
    return rowCountRef.current;
  }, [response?.apiData?.count]);

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    [],
  );
  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
  }, [memberTypeQuery]);

  const { data: materialTypeQuery } = GetMaterialTypeValues();
  const [materialTypeOptions, setMaterialTypeOptions] = useState<
    IDropdownModel[]
  >([]);
  useEffect(() => {
    setMaterialTypeOptions(materialTypeQuery?.apiData || []);
  }, [materialTypeQuery]);

  const { control, handleSubmit, reset } = useForm<ITopBooksFilterFormValues>({
    mode: "all",
    defaultValues: {
      memberTypeID: null,
      materialTypeID: null,
    },
  });

  useEffect(() => {
    reset({
      memberTypeID: topBooksPostModel.filterModel?.memberTypeID ?? null,
      materialTypeID: topBooksPostModel.filterModel?.materialTypeID ?? null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    topBooksPostModel.filterModel?.memberTypeID,
    topBooksPostModel.filterModel?.materialTypeID,
  ]);

  const onSubmit = handleSubmit((data) => {
    handleTopBooksFiltering({
      memberTypeID: data.memberTypeID,
      materialTypeID: data.materialTypeID,
      fromDate,
      toDate,
    });
  });

  const handleClear = () => {
    handleTopBooksFiltering({
      memberTypeID: null,
      materialTypeID: null,
      fromDate,
      toDate,
    });
  };

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      accessionNumbers: false,
    });

  const [exportLoading, setExportLoading] = useState(false);
  const handleExport = async () => {
    setExportLoading(true);
    await api.downloadExcel(
      "api/Dashboard/exportTopFrequentlyUsedBooks",
      "TopFrequentlyUsedBooks_",
      {
        memberTypeID: topBooksPostModel.filterModel?.memberTypeID ?? null,
        materialTypeID: topBooksPostModel.filterModel?.materialTypeID ?? null,
        fromDate,
        toDate,
      },
      setExportLoading,
    );
  };

  const columns: GridColDef<ITopFrequentlyUsedBooks>[] = useMemo(
    () => [
      {
        field: "rank",
        headerName: "Rank",
        width: 90,
        flex: 0.5,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={String(params.value ?? "")}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "title",
        headerName: "Title",
        flex: 1.5,
        renderCell: (params) => (
          <Tooltip title={String(params.value ?? "")}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "accessionNumbers",
        headerName: "Accession No(s).",
        flex: 1.5,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title={String(params.value ?? "")}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "materialTypeName",
        headerName: "Material Type",
        sortable: false,
        flex: 1,
        renderCell: (params) => (
          <Tooltip title={String(params.value ?? "")}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
      {
        field: "totalIssueCount",
        headerName: "Total Issue Count",
        flex: 1,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <Tooltip title={String(params.value ?? "")}>
            <span>{params.value}</span>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  return (
    <MainCard
      title="Top Frequently Used Books"
      secondary={
        <ExportButton
          groupName="TopFrequentlyUsedBooks"
          fileName="TopFrequentlyUsedBooks"
          reportTitle="Top Frequently Used Books"
          params={() => ({
            memberTypeID: topBooksPostModel.filterModel?.memberTypeID ?? null,
            materialTypeID:
              topBooksPostModel.filterModel?.materialTypeID ?? null,
            fromDate,
            toDate,
          })}
          disabled={rowCount === 0}
          size="small"
          buttonText="Export"
        />
      }
    >
      <>
        <Box component="form" onSubmit={onSubmit}>
          <Card elevation={0} variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4} lg={3}>
                  <SelectField
                    options={memberTypeOptions}
                    control={control}
                    name="memberTypeID"
                    label="Member Type"
                    placeholder="Select Member Type"
                  />
                </Grid>
                <Grid item xs={12} md={4} lg={3}>
                  <SelectField
                    options={materialTypeOptions}
                    control={control}
                    name="materialTypeID"
                    label="Material Type"
                    placeholder="Select Material Type"
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  lg={3}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <LoadingButton
                    variant="contained"
                    sx={{ mr: 1 }}
                    type="submit"
                    loading={isFetching}
                    disabled={isFetching}
                  >
                    Search
                  </LoadingButton>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleClear}
                    disabled={isFetching}
                  >
                    Clear
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h5" gutterBottom>
              Top 10 (Current Filter)
            </Typography>
            <TopBooksChart data={response?.apiData?.data} />
            <Divider sx={{ my: 2 }} />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              sx={{ mb: 1 }}
              control={
                <Switch
                  checked={!!columnVisibilityModel.accessionNumbers}
                  onChange={(event) =>
                    setColumnVisibilityModel((prev) => ({
                      ...prev,
                      accessionNumbers: event.target.checked,
                    }))
                  }
                />
              }
              label="Show Accession No(s)."
            />
            <Box sx={{ height: 450 }}>
              <StyledDataGrid
                density="compact"
                disableRowSelectionOnClick
                getRowId={(item) => item.uniqueID}
                columns={columns}
                pageSizeOptions={[10, 20, 50, 100]}
                initialState={{
                  pagination: {
                    paginationModel: { ...topBooksPostModel.pagingModel },
                  },
                }}
                sx={{
                  "& .MuiDataGrid-columnHeaderTitle": { fontSize: "13px" },
                  "& .MuiDataGrid-cell": {
                    whiteSpace: "normal",
                    alignContent: "center",
                    fontSize: 14,
                  },
                }}
                sortModel={topBooksPostModel.sortModel}
                onSortModelChange={handleTopBooksSorting}
                onPaginationModelChange={handleTopBooksPagination}
                paginationMode="server"
                sortingMode="server"
                rows={response?.apiData?.data ?? []}
                rowCount={rowCount}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={setColumnVisibilityModel}
                loading={isFetching}
                
              />
            </Box>
          </Grid>
        </Grid>
      </>
    </MainCard>
  );
};

export default TopBooksSection;
